import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateAdResponseBody } from '~/lib/api/modules/Ads';
import type { IUseUpdateAdMutationValues } from '~/lib/api/modules/Ads/hooks/useUpdateAdMutation';
import type { IAdFormValues } from './AdForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAdQuery } from '~/lib/api/modules/Ads/hooks/useAdQuery';
import { useUpdateAdMutation } from '~/lib/api/modules/Ads/hooks/useUpdateAdMutation';
import { AdForm } from './AdForm';

export type IUpdateAdFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateAdResponseBody,
    inputValues: IUseUpdateAdMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  adId: string;
};

export const UpdateAdForm: React.FC<IUpdateAdFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, adId } = props;
  const adQuery = useAdQuery({ appId, adId });
  const updateAdMutation = useUpdateAdMutation();
  const [disabled, setDisabled] = useState(false);

  const ad = adQuery.data;
  const locationOpts = ad?.locationOpts?.split(':');

  const handleSubmit: SubmitHandler<IAdFormValues> = (values) => {
    if (updateAdMutation.isLoading) {
      return;
    }

    return updateAdMutation.mutateAsync(
      {
        appId,
        adId,
        body: {
          active: !values.disabled,
          campaignId: values.name,
          format: values.feedFormat,
          location: values.location,
          locationOpts:
            values.location === 'feed'
              ? [
                  values.feedPosition || '0',
                  values.feedRecurrentStep || '0',
                  values.feedIndex || '0',
                  values.feedReccurentMaxIndex || '0',
                ].join(':')
              : values.location === 'article'
                ? [values.articlePosition].join('::')
                : undefined,
          media: values.medias?.[0]?.id,
          mediaType: values.medias?.[0]?.type,
          url: values.url,
          limits: {
            maxDisplays: values.maxDisplays ? Number(values.maxDisplays) : 0,
            maxClicks: values.maxClicks ? Number(values.maxClicks) : 0,
            notBefore: values.startAt,
            notAfter: values.endAt,
          },
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock draft={disabled} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Suspense
        loading={adQuery.isLoading}
        error={adQuery.error}
        first
      >
        {ad && (
          <AdForm
            mode="edition"
            appId={appId}
            cancelUrl={cancelUrl}
            defaultValues={{
              name: ad.campaignId,
              url: ad.url,
              medias: ad.media
                ? [
                    {
                      type: ad.mediaType,
                      id: ad.media,
                    },
                  ]
                : [],
              location: ad.location,
              feedFormat: ad.format,
              feedPosition:
                ad.location === 'feed' ? locationOpts?.[0] || '0' : undefined,
              feedIndex:
                ad.location === 'feed' && !isNaN(Number(locationOpts?.[1]))
                  ? locationOpts?.[1]
                  : '0',
              feedRecurrentStep:
                ad.location === 'feed' && !isNaN(Number(locationOpts?.[2]))
                  ? locationOpts?.[2]
                  : '0',
              feedReccurentMaxIndex:
                ad.location === 'feed' && !isNaN(Number(locationOpts?.[3]))
                  ? Number(locationOpts?.[3])
                    ? locationOpts?.[3]
                    : ''
                  : '3',
              articlePosition:
                ad.location === 'article' ? locationOpts?.[0] : undefined,
              maxDisplays: ad.limits?.maxDisplays
                ? String(ad.limits?.maxDisplays)
                : undefined,
              maxClicks: ad.limits?.maxClicks
                ? String(ad.limits?.maxClicks)
                : undefined,
              startAt: ad.limits?.notBefore,
              endAt: ad.limits?.notAfter,
              disabled: !ad.active,
            }}
            onSubmit={handleSubmit}
            onDisabledStatusChange={setDisabled}
            first
          />
        )}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
